import React, { createContext, useContext, useEffect, useState } from "react";
import api from "@/lib/api";

/**
 * Runtime brand context. Loads from /api/brand/public on mount and re-applies
 * palette + font tokens as CSS variables on <html>. Admins call `.refresh()`
 * after editing brand settings to update the live site immediately.
 */
const BrandContext = createContext(null);

const CSS_VAR_MAP = {
  primary_color: "--gcw-primary",
  secondary_color: "--gcw-secondary",
  accent_color: "--gcw-accent",
  background_color: "--gcw-bg",
  text_color: "--gcw-text",
  border_radius: "--gcw-radius",
};

export function BrandProvider({ children }) {
  const [brand, setBrand] = useState(null);
  const [loading, setLoading] = useState(true);

  const applyToRoot = (b) => {
    if (!b || typeof document === "undefined") return;
    const root = document.documentElement;
    Object.entries(CSS_VAR_MAP).forEach(([k, v]) => {
      if (b[k]) root.style.setProperty(v, b[k]);
    });
    if (b.heading_font) root.style.setProperty("--gcw-font-heading", `"${b.heading_font}"`);
    if (b.body_font) root.style.setProperty("--gcw-font-body", `"${b.body_font}"`);
    if (b.favicon_url) {
      const link = document.querySelector("link[rel='icon']") || Object.assign(document.createElement("link"), { rel: "icon" });
      link.href = b.favicon_url;
      if (!link.parentNode) document.head.appendChild(link);
    }
    if (b.brand_name && document.title.includes("Gulf Coast Work") && b.brand_name !== "Gulf Coast Work") {
      document.title = document.title.replace(/Gulf Coast Work/g, b.brand_name);
    }
  };

  const refresh = async () => {
    try {
      const { data } = await api.get("/brand/public");
      setBrand(data);
      applyToRoot(data);
      return data;
    } catch (_) {
      return null;
    } finally { setLoading(false); }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, []);

  return (
    <BrandContext.Provider value={{ brand, loading, refresh }}>
      {children}
    </BrandContext.Provider>
  );
}

export function useBrand() {
  const ctx = useContext(BrandContext);
  return ctx || { brand: null, loading: false, refresh: async () => null };
}
